import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCard } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { Router } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { ApplicantPqqSectionsComponent } from '../applicant-pqq-sections.component';
import { MaxLength } from '@/app/enums/max-length.enum';
import { ValidationPatterns } from '@/app/enums/patterns.enum';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { CountryService } from '@/app/services/shared/country';

@Component({
  selector: 'app-company-general-information',
  standalone: true,
  imports: [
    MatExpansionModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCard,
    MatRadioModule,
    ApplicantPqqSectionsComponent,
    MatSelectModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './company-general-information.component.html',
  styleUrl: './company-general-information.component.css',
})
export class CompanyGeneralInformationComponent implements OnInit, OnChanges {
  @Input() applicantData: any;
  @Input() isEdit: boolean;
  @Input() isFetchApplicantData: boolean;
  formGroup: FormGroup;
  maxLength = MaxLength;
  validationPatterns = ValidationPatterns;
  yearsList: number[] = [];
  currencySymbol: any;
  countries: any;
  countryName: string;
  constructor(
    private fb: FormBuilder,
    private toast: NgToastService,
    private projectsService: ProjectsService,
    private router: Router,
    private TenderingService: TenderingService,
    private ApplicantPqqSectionsComponent: ApplicantPqqSectionsComponent,
    private countryServices: CountryService
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      company_name: ['', Validators.required],
      address: ['', Validators.required],
      company_reg_no: ['', Validators.required],
      company_vat_no: ['', Validators.required],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validationPatterns.PhoneNumber),
        ],
      ],
      email: ['', Validators.required],
      website: ['', Validators.required],
      contact_person: ['', Validators.required],
      contact_person_position: ['', Validators.required],
      contact_person_phone: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validationPatterns.PhoneNumber),
        ],
      ],
      contact_person_email: ['', Validators.required],
      legal_status: ['', Validators.required],
      year_established: ['', Validators.required],
      parent_company: ['', Validators.required],
      subcontractor_count: ['', Validators.required],
      direct_employees: [null, Validators.required],
      annual_turnover: [null, Validators.required],
      completed_projects: [null, Validators.required],
      ongoing_projects: [null, Validators.required],
    });
    this.patchFormValues();
    this.generateYearsList();
    // function Call on Dialog open
    this.countryName = this.countryServices.getProjectCountryName();
    this.getCountriesList();
  }

  getCountriesList(): void {
    this.projectsService.getCountries().subscribe({
      next: (response: any) => {
        this.countries = response.data;
        this.countries.forEach((country) => {
          if (country.name === this.countryName) {
            this.currencySymbol = country.symbol;
          }
        });
      },
      error: (error) => {
        console.error('API error:', error);
      },
    });
  }
  numberToWords(num: any) {
    const belowTwenty = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      '',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];
    const thousands = ['', 'thousand', 'million', 'billion', 'trillion'];

    function helper(n) {
      if (n === 0) return '';
      else if (n < 20) return belowTwenty[n] + ' ';
      else if (n < 100) return tens[Math.floor(n / 10)] + ' ' + helper(n % 10);
      else
        return belowTwenty[Math.floor(n / 100)] + ' hundred ' + helper(n % 100);
    }

    let word = '';
    let index = 0;

    while (num > 0) {
      if (num % 1000 !== 0) {
        word = helper(num % 1000) + thousands[index] + ' ' + word;
      }
      num = Math?.floor(num / 1000);
      index++;
    }

    return word.trim();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.formGroup && changes['applicantData']) {
      this.patchFormValues();
    }
    if (
      changes['isFetchApplicantData'] &&
      !changes['isFetchApplicantData'].firstChange
    ) {
      this.patchFormValues();
    }
  }

  generateYearsList(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 100; i--) {
      this.yearsList.push(i);
    }
  }

  patchFormValues() {
    if (this.isEdit && this.applicantData?.company_name) {
      this.formGroup.patchValue({
        company_name: this.applicantData?.company_name || '',
        address: this.applicantData?.address || '',
        company_reg_no: this.applicantData?.company_reg_no || '',
        company_vat_no: this.applicantData?.company_vat_no || '',
        phone: this.applicantData?.phone || '',
        email: this.applicantData?.email || '',
        website: this.applicantData?.website || '',
        contact_person: this.applicantData?.contact_person || '',
        contact_person_position:
          this.applicantData?.contact_person_position || '',
        contact_person_phone: this.applicantData?.contact_person_phone || '',
        contact_person_email: this.applicantData?.contact_person_email || '',
        legal_status: this.applicantData?.legal_status || '',
        year_established: this.applicantData?.year_established || '',
        parent_company: this.applicantData?.parent_company || '',
        subcontractor_count: this.applicantData?.subcontractor_count || '',
        direct_employees: this.applicantData?.direct_employees || [],
        annual_turnover: this.applicantData?.annual_turnover || null,
        completed_projects: this.applicantData?.completed_projects || [],
        ongoing_projects: this.applicantData?.ongoing_projects || [],
      });
    }
  }

  handleBack() {
    this.ApplicantPqqSectionsComponent.previousStep();
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const charCode = event.key;
    if (!/[0-9]/.test(charCode) && !allowedKeys.includes(charCode)) {
      event.preventDefault();
    }
  }

  prepareDataToSubmit() {
    const dataToSubmit = new FormData();

    // Dynamically append all form controls to FormData
    Object.keys(this.formGroup.controls).forEach((key) => {
      const value = this.formGroup.get(key)?.value;
      if (value !== null && value !== undefined && value !== '') {
        dataToSubmit.append(key, value);
      }
    });

    return dataToSubmit;
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const dataToSubmit = this.prepareDataToSubmit();

      this.projectsService.projectPQQResponseStep1(dataToSubmit).subscribe({
        next: () => {
          this.toast.success('Pre Qualification submitted', 'SUCCESS');
          this.ApplicantPqqSectionsComponent.nextStep();
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      if (this.formGroup.invalid) {
        Object.keys(this.formGroup.controls).forEach((field) => {
          const control = this.formGroup.get(field);
          if (control?.invalid) {
            console.log(`Field: ${field}, Errors: `, control.errors);
          }
        });
      }

      this.formGroup.markAllAsTouched();
      this.toast.warning('Please fill the required fields', 'WARNING');
    }
  }
}
